import { Box, Button, Container, SxProps } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import PaddedBox from "~/components/PaddedBox";
import { BackArrowText } from "~/pages/ProjectsPage/components/BackArrowText";
import { useAppSelector } from "~/redux/store/store";
import { DialogAction } from "~/types/dialogAction";

type Props<TItem, TId extends string | number, TForm> = {
  action: DialogAction | null;
  id: TId | null;
  onClose: () => void;
  getItemById: (id: TId) => Promise<TItem | null> | TItem | null;

  editItem?: (formData: TForm, id: TId) => void | Promise<unknown>;
  addItem?: (formData: TForm) => void | Promise<unknown>;
  formId: string;
  renderFormComponent: (
    props: FormComponentProps<TItem, TForm>,
  ) => React.ReactNode;
};

export type FormComponentProps<TItem, TForm> = {
  defaultValues: TItem | null;
  onSubmit: (d: TForm) => void;
  readOnly: boolean;
  formId: string;
};

export const ListPageView = <
  TItem,
  TForm,
  TId extends string | number = number,
>({
  action,
  id,
  onClose,
  getItemById,

  editItem,
  addItem,
  formId,
  renderFormComponent,
}: Props<TItem, TId, TForm>): JSX.Element | null => {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = React.useState<TItem | null>(null);
  const isMobile = useAppSelector((s) => s.general.isMobile);

  React.useEffect(() => {
    const getItem = async () => {
      if (id === null) {
        setSelectedItem(null);
        return;
      }
      const item = await getItemById(id);

      setSelectedItem(item);
    };
    void getItem();
  }, [getItemById, id]);

  const isViewing = id !== null && action === "view";
  const isEditing = id !== null && action === "edit";
  const isAdding = id === null && action === "add";

  // const title = React.useMemo(() => {
  //   return getTitle(action, selectedItem);
  // }, [action, getTitle, selectedItem]);

  const handleSubmit = React.useCallback(
    (d: TForm) => {
      if (isEditing && editItem) {
        Promise.resolve(editItem(d, id as TId))
          .then(() => {
            onClose();
          })
          .catch(() => {});
      } else if (isAdding && addItem) {
        Promise.resolve(addItem(d))
          .then(() => {
            onClose();
          })
          .catch(() => {});
      }
    },
    [addItem, editItem, id, isAdding, isEditing, onClose],
  );

  const dialogContentMobileStyles = React.useMemo((): SxProps => {
    if (isMobile) return { padding: "2px" };
    return {};
  }, [isMobile]);

  if (!isViewing && !isEditing && !isAdding) return null;

  return (
    <PaddedBox>
      <Container
        maxWidth={isMobile ? false : "xl"}
        sx={{
          display: "grid",
          justifyContent: "center",
          gridTemplateColumns: isMobile ? "100%" : "90%",
          padding: isMobile ? "0px" : "16px",
          overflow: "auto",
          rowGap: "15px",
        }}
      >
        <BackArrowText
          url="/ais-vessels/cameras"
          text={t("vessels:cameras-list")}
        />

        <Box sx={dialogContentMobileStyles}>
          {renderFormComponent({
            defaultValues: selectedItem,
            formId,
            onSubmit: handleSubmit,
            readOnly: isViewing,
          })}
        </Box>

        {isViewing ? null : (
          <Button
            type="submit"
            form={formId}
            variant="contained"
            color="secondary"
            sx={{ m: "auto" }}
          >
            {t("general:save")}
          </Button>
        )}
      </Container>
    </PaddedBox>
  );
};
